import * as React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import Remove from '@material-ui/icons/Remove';
import RepeatIcon from '@material-ui/icons/Repeat';
import CheckIcon from '@material-ui/icons/Check';
import WarningIcon from '@material-ui/icons/Warning';
import { TextInputField } from './TextInputField';
import { useCallback, useMemo, useState } from 'react';
import { getNowDateString } from '../../../shared/utils';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { DateField } from './DateField';
import { AppointmentData, RecurringUnit, RecurringConfig } from '../../../models/api-models';
import moment from 'moment';
import { useBreakpoints } from '../../../shared/hooks/useBreakpoints';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { SelectTextField } from './SelectTextField';
import { NumberInputField } from './NumberInputField';
import styled from 'styled-components';
import AppButton from '../../AppButton';

const PAGINATION_THRESHOLD = 10;

const MonthTabsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XS);
    margin-bottom: var(--spacer-S);
`;

const MonthTab = styled.button<React.ButtonHTMLAttributes<HTMLButtonElement> & { $active: boolean }>`
    padding: 4px 10px;
    border-radius: 12px;
    border: var(--border-thin) solid ${p => p.$active ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    background: ${p => p.$active ? 'var(--bg-hover-light)' : 'transparent'};
    color: ${p => p.$active ? 'var(--appBlue)' : 'var(--text-muted)'};
    font-size: 0.8rem;
    font-weight: ${p => p.$active ? 600 : 400};
    cursor: pointer;
    white-space: nowrap;
    &:hover { border-color: var(--appBlue); color: var(--appBlue); }
`;

export interface DateInterval {
    from: string
    to: string
    description: string
    recurring?: RecurringConfig
}

export interface DateIntervalFieldProp {
    id?: string,
    label?: string,
    values: DateInterval[],
    onChange: (values: DateInterval[]) => void,
    disabled?: boolean,
    minDates?: number,
    maxDates?: number,
    hideDescription?: boolean,
    type?: 'date' | 'datetime',
    enableRecurring?: boolean,
    addViaModal?: boolean,
    addModalTitle?: string,
    onValidateInterval?: (interval: DateInterval) => Promise<AppointmentData[]>,
}

export const DateIntervalField: React.FC<DateIntervalFieldProp> = (props) => {
    const {
        id,
        label,
        values,
        onChange,
        disabled,
        minDates,
        maxDates,
        hideDescription,
        type = 'datetime',
        enableRecurring = false,
        addViaModal = false,
        addModalTitle,
        onValidateInterval,
    } = props;

    const t = useTranslations()
    const bp = useBreakpoints()
    const [editRecurringIdx, setEditRecurringIdx] = useState<number | null>(null)

    const RECURRING_TAB = '__recurring__';

    const isPaginated = values.length > PAGINATION_THRESHOLD;

    const hasRecurring = useMemo(() => isPaginated && values.some(v => !!v.recurring), [isPaginated, values]);

    const months = useMemo(() => {
        if (!isPaginated) return [];
        const seen = new Set<string>();
        return values
            .map(v => v.from.slice(0, 7))
            .filter(m => { if (seen.has(m)) return false; seen.add(m); return true; })
            .sort();
    }, [isPaginated, values]);

    const [selectedMonth, setSelectedMonth] = useState<string>(() => {
        if (!isPaginated) return '';
        const now = moment().format('YYYY-MM');
        const ms = [...new Set(values.map(v => v.from.slice(0, 7)))].sort();
        return ms.find(m => m >= now) ?? ms[ms.length - 1] ?? now;
    });

    const effectiveMonth = useMemo(() => {
        if (!isPaginated) return '';
        if (selectedMonth === RECURRING_TAB) return RECURRING_TAB;
        if (months.includes(selectedMonth)) return selectedMonth;
        const now = moment().format('YYYY-MM');
        return months.find(m => m >= now) ?? months[months.length - 1] ?? now;
    }, [isPaginated, months, selectedMonth]);

    const pagedEntries = useMemo((): [number, DateInterval][] => {
        if (!isPaginated) return values.map((v, i) => [i, v]);
        return values
            .map((v, i) => [i, v] as [number, DateInterval])
            .filter(([, v]) => effectiveMonth === RECURRING_TAB ? !!v.recurring : !v.recurring && v.from.slice(0, 7) === effectiveMonth);
    }, [isPaginated, values, effectiveMonth]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [modalInterval, setModalInterval] = useState<DateInterval>({ from: '', to: '', description: '' });
    const [modalEditRecurring, setModalEditRecurring] = useState(false);
    const [modalConflicts, setModalConflicts] = useState<AppointmentData[] | null>(null);
    const [modalValidating, setModalValidating] = useState(false);

    const buildDefaultInterval = (): DateInterval => {
        const from = isPaginated && effectiveMonth !== RECURRING_TAB
            ? `${effectiveMonth}-01T00:00`
            : `${getNowDateString()}T00:00`;
        const to = isPaginated && effectiveMonth !== RECURRING_TAB
            ? `${effectiveMonth}-01T23:59`
            : `${getNowDateString()}T23:59`;
        return { from, to, description: '' };
    };

    const onAddNew = () => {
        if (addViaModal) {
            setModalInterval(buildDefaultInterval());
            setModalEditRecurring(false);
            setModalConflicts(null);
            setShowAddModal(true);
        } else {
            onChange(values.concat(buildDefaultInterval()));
        }
    };

    const onConfirmAdd = async (force = false) => {
        if (!force && onValidateInterval) {
            setModalValidating(true);
            try {
                const conflicts = await onValidateInterval(modalInterval);
                if (conflicts.length > 0) {
                    setModalConflicts(conflicts);
                    return;
                }
            } finally {
                setModalValidating(false);
            }
        }
        onChange(values.concat(modalInterval));
        setShowAddModal(false);
        setModalConflicts(null);
    };

    const onChangeItem = useCallback(
        (idx: number, newVal: DateInterval) => onChange(values.map((v, i) => idx === i ? newVal : v)),
        [onChange, values])

    const toggleRecurring = (idx: number) => {
        const currentInterval = values[idx]
        if (currentInterval.recurring) {
            onChangeItem(idx, { ...currentInterval, recurring: undefined })
        } else {
            const defaultRecurring: RecurringConfig = {
                endDate: moment(currentInterval.from).add(1, 'month').format("YYYY-MM-DD"),
                unit: RecurringUnit.Weeks,
                value: 1,
            }
            onChangeItem(idx, { ...currentInterval, recurring: defaultRecurring })
            setEditRecurringIdx(idx)
        }
    }

    const recurringLabel = (recurring: RecurringConfig): string | undefined => {
        const unit: TK = recurring.unit === RecurringUnit.Days
            ? (recurring.value ?? 1) == 1 ? TK.day : TK.days
            : recurring.unit === RecurringUnit.Weeks
                ?  (recurring.value ?? 1) == 1 ? TK.week : TK.weeks
                : recurring.unit === RecurringUnit.Months
                    ? (recurring.value ?? 1) == 1 ? TK.month : TK.months
                    : TK.days

        const str = `${t(TK.every)} ${recurring.value} ${t(unit).toLowerCase()}`
        return recurring.endDate ? `${str}, ${t(TK.until).toLowerCase()} ${recurring.endDate}` : str;
    }

    const periodUnitsToEnum: Record<string, RecurringUnit> = {
        [t(TK.days)]: RecurringUnit.Days,
        [t(TK.weeks)]: RecurringUnit.Weeks,
        [t(TK.months)]: RecurringUnit.Months,
        [t(TK.day)]: RecurringUnit.Days,
        [t(TK.week)]: RecurringUnit.Weeks,
        [t(TK.month)]: RecurringUnit.Months,
    }

    const periodUnitsToLabelPlural: Record<RecurringUnit, string> = {
        [RecurringUnit.Days]: t(TK.days),
        [RecurringUnit.Weeks]: t(TK.weeks),
        [RecurringUnit.Months]: t(TK.months),
    }

    const periodUnitsToLabelSingular: Record<RecurringUnit, string> = {
        [RecurringUnit.Days]: t(TK.day),
        [RecurringUnit.Weeks]: t(TK.week),
        [RecurringUnit.Months]: t(TK.month),
    }

    return (
    <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
            {label && <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>{label}</label>}
            {isPaginated && (
                <MonthTabsContainer>
                    {hasRecurring && (
                        <MonthTab
                            type="button"
                            $active={effectiveMonth === RECURRING_TAB}
                            onClick={() => setSelectedMonth(RECURRING_TAB)}
                        >
                            <RepeatIcon style={{ fontSize: '0.9rem', verticalAlign: 'middle', marginRight: '3px' }} />
                            {t(TK.recurring)}
                            <span style={{ marginLeft: '4px', opacity: 0.6, fontSize: '0.75rem' }}>
                                ({values.filter(v => !!v.recurring).length})
                            </span>
                        </MonthTab>
                    )}
                    {months.map(m => (
                        <MonthTab
                            key={m}
                            type="button"
                            $active={m === effectiveMonth}
                            onClick={() => setSelectedMonth(m)}
                        >
                            {moment(m, 'YYYY-MM').format('MMM YYYY')}
                            <span style={{ marginLeft: '4px', opacity: 0.6, fontSize: '0.75rem' }}>
                                ({values.filter(v => !v.recurring && v.from.slice(0, 7) === m).length})
                            </span>
                        </MonthTab>
                    ))}
                </MonthTabsContainer>
            )}
            {
                pagedEntries.map(([origIdx, dateInterval]) => (
                    <div key={origIdx} style={{order: pagedEntries.filter(([, v]) => v.from < dateInterval.from).length, display: 'flex', flexDirection: 'column'}}>
                        <div style={{display: 'flex', flexDirection: bp.sm ? 'column' : 'row', gap: 'var(--spacer-XS)'}}>
                            <DateField
                                id={id ? `${id}-${origIdx}-from` : undefined}
                                style={{ maxWidth: 'fit-content' }}
                                value={dateInterval.from}
                                type={type}
                                onChange={from => onChangeItem(origIdx, { ...dateInterval, from })}
                            />
                            <DateField
                                id={id ? `${id}-${origIdx}-to` : undefined}
                                style={{ maxWidth: 'fit-content' }}
                                value={dateInterval.to}
                                type={type}
                                onChange={to => onChangeItem(origIdx, { ...dateInterval, to })}
                            />
                            {!hideDescription && <TextInputField
                                id={id ? `${id}-${origIdx}-description` : undefined}
                                style={{ flexGrow: 1 }}
                                value={dateInterval.description}
                                placeholder={t(TK.description)}
                                onChange={description => onChangeItem(origIdx, { ...dateInterval, description })}
                            />}
                            <div style={{display: 'flex', gap: 'var(--spacer-XS)'}}>
                                {enableRecurring && !disabled && (
                                    <Tooltip title={t(TK.makeItRecurring)}>
                                        <IconButton
                                            id={id ? `${id}-${origIdx}-recurring-button` : undefined}
                                            onClick={() => toggleRecurring(origIdx)}
                                            size="small"
                                            style={{
                                                color: dateInterval.recurring ? 'var(--appBlue)' : 'var(--text-muted)'
                                            }}
                                        >
                                            <RepeatIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {!disabled && (values.length > (minDates || 0)) && (
                                    <Tooltip title={t(TK.remove)}>
                                        <IconButton
                                            id={id ? `${id}-${origIdx}-remove-button` : undefined}
                                            onClick={() => {
                                                onChange(values.filter((_, i) => i !== origIdx))
                                                setEditRecurringIdx(null)
                                            }}
                                            size="small"
                                        >
                                            <Remove/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        {dateInterval.recurring && editRecurringIdx !== origIdx && (
                            <AppButton
                                color='link'
                                style={{ position: 'relative', top: '-5px', maxWidth: 'fit-content', fontSize: '0.8rem', color: 'var(--text-muted)' }}
                                onClick={() => setEditRecurringIdx(editRecurringIdx === origIdx ? null : origIdx)}
                            >
                                {recurringLabel(dateInterval.recurring)}
                            </AppButton>
                        )}
                        {dateInterval.recurring && editRecurringIdx === origIdx && (
                            <div style={{
                                backgroundColor: 'var(--bg-light)',
                                borderRadius: '4px',
                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                padding: 'var(--spacer-S)',
                                display: 'flex',
                                gap: 'var(--spacer-XS)',
                                alignItems: 'center',
                                marginTop: 'var(--spacer-XS)',
                            }}>
                                <NumberInputField
                                    id={id ? `${id}-${origIdx}-recurring-interval` : undefined}
                                    prepend={t(TK.every)}
                                    type="number"
                                    min={1}
                                    style={{ maxWidth: '110px' }}
                                    value={dateInterval.recurring.value}
                                    onChange={value => onChangeItem(origIdx, {
                                            ...dateInterval,
                                            recurring: {
                                                ...dateInterval.recurring!,
                                                value: value ?? 1,
                                            },
                                        },
                                    )}
                                />
                                <SelectTextField
                                    id={id ? `${id}-${origIdx}-recurring-unit` : undefined}
                                    style={{ maxWidth: '150px' }}
                                    value={dateInterval.recurring.value > 1 ? periodUnitsToLabelPlural[dateInterval.recurring.unit] : periodUnitsToLabelSingular[dateInterval.recurring.unit]}
                                    options={dateInterval.recurring.value > 1 ? Object.values(periodUnitsToLabelPlural) : Object.values(periodUnitsToLabelSingular)}
                                    onValueChanged={label => (onChangeItem(origIdx, {
                                        ...dateInterval,
                                        recurring: {
                                            ...dateInterval.recurring!,
                                            unit: periodUnitsToEnum[label],
                                        },
                                    }))}
                                />
                                <DateField
                                    id={id ? `${id}-${origIdx}-recurring-enddate` : undefined}
                                    label={t(TK.until)}
                                    style={{ maxWidth: '150px' }}
                                    value={dateInterval.recurring.endDate || ''}
                                    type="date"
                                    onChange={endDate => onChangeItem(origIdx, {
                                        ...dateInterval,
                                        recurring: {
                                            ...dateInterval.recurring!,
                                            endDate,
                                        }
                                    })}
                                />
                                <AppButton outline color='success' onClick={() => setEditRecurringIdx(null)}><CheckIcon/></AppButton>
                            </div>
                        )}
                    </div>
                ))
            }
            { !disabled && (!maxDates || values.length < maxDates) &&
                <div style={{width: '100%', textAlign: 'center', marginTop: 'var(--spacer-S)', order: 100000}}>
                    <IconButton id={'add-service-button'} onClick={onAddNew} size="small">
                        <AddCircleIcon />
                    </IconButton>
                    <span style={{ verticalAlign: 'text-top', cursor: 'pointer' }} onClick={onAddNew}>
                        {t(TK.add)}
                    </span>
                </div>
            }
        </div>
        {addViaModal && (
            <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)}>
                <ModalHeader toggle={() => setShowAddModal(false)}>
                    {addModalTitle ?? t(TK.add)}
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-XS)' }}>
                            <DateField
                                label={t(TK.from)}
                                style={{ flex: 1, minWidth: '160px' }}
                                value={modalInterval.from}
                                type={type}
                                onChange={from => setModalInterval(v => ({ ...v, from }))}
                            />
                            <DateField
                                label={t(TK.to)}
                                style={{ flex: 1, minWidth: '160px' }}
                                value={modalInterval.to}
                                type={type}
                                onChange={to => setModalInterval(v => ({ ...v, to }))}
                            />
                        </div>
                        {!hideDescription && (
                            <TextInputField
                                label={t(TK.description)}
                                value={modalInterval.description}
                                onChange={description => setModalInterval(v => ({ ...v, description }))}
                            />
                        )}
                        {enableRecurring && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                    <Tooltip title={t(TK.makeItRecurring)}>
                                        <IconButton
                                            onClick={() => {
                                                if (modalInterval.recurring) {
                                                    setModalInterval(v => ({ ...v, recurring: undefined }));
                                                    setModalEditRecurring(false);
                                                } else {
                                                    setModalInterval(v => ({
                                                        ...v,
                                                        recurring: {
                                                            endDate: moment(v.from).add(1, 'month').format('YYYY-MM-DD'),
                                                            unit: RecurringUnit.Weeks,
                                                            value: 1,
                                                        },
                                                    }));
                                                    setModalEditRecurring(true);
                                                }
                                            }}
                                            size="small"
                                            style={{ color: modalInterval.recurring ? 'var(--appBlue)' : 'var(--text-muted)' }}
                                        >
                                            <RepeatIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {modalInterval.recurring && !modalEditRecurring && (
                                        <AppButton
                                            color="link"
                                            style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: 0 }}
                                            onClick={() => setModalEditRecurring(true)}
                                        >
                                            {recurringLabel(modalInterval.recurring)}
                                        </AppButton>
                                    )}
                                </div>
                                {modalInterval.recurring && modalEditRecurring && (
                                    <div style={{
                                        backgroundColor: 'var(--bg-light)',
                                        borderRadius: '4px',
                                        border: 'var(--border-thin) solid var(--appLigthGrey)',
                                        padding: 'var(--spacer-S)',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 'var(--spacer-XS)',
                                        alignItems: 'center',
                                        marginTop: 'var(--spacer-XS)',
                                    }}>
                                        <NumberInputField
                                            prepend={t(TK.every)}
                                            type="number"
                                            min={1}
                                            style={{ maxWidth: '110px' }}
                                            value={modalInterval.recurring.value}
                                            onChange={value => setModalInterval(v => ({
                                                ...v,
                                                recurring: { ...v.recurring!, value: value ?? 1 },
                                            }))}
                                        />
                                        <SelectTextField
                                            style={{ maxWidth: '150px' }}
                                            value={modalInterval.recurring.value > 1
                                                ? periodUnitsToLabelPlural[modalInterval.recurring.unit]
                                                : periodUnitsToLabelSingular[modalInterval.recurring.unit]}
                                            options={modalInterval.recurring.value > 1
                                                ? Object.values(periodUnitsToLabelPlural)
                                                : Object.values(periodUnitsToLabelSingular)}
                                            onValueChanged={label => setModalInterval(v => ({
                                                ...v,
                                                recurring: { ...v.recurring!, unit: periodUnitsToEnum[label] },
                                            }))}
                                        />
                                        <DateField
                                            label={t(TK.until)}
                                            style={{ maxWidth: '150px' }}
                                            value={modalInterval.recurring.endDate || ''}
                                            type="date"
                                            onChange={endDate => setModalInterval(v => ({
                                                ...v,
                                                recurring: { ...v.recurring!, endDate },
                                            }))}
                                        />
                                        <AppButton outline color="success" onClick={() => setModalEditRecurring(false)}>
                                            <CheckIcon />
                                        </AppButton>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalBody style={{ paddingTop: 0 }}>
                    {modalConflicts && modalConflicts.length > 0 && (
                        <div style={{ backgroundColor: 'var(--warning-bg)', border: 'var(--border-thin) solid var(--warning-border)', borderRadius: 4, padding: 'var(--spacer-S)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', color: 'var(--warning-color)', fontWeight: 600 }}>
                                <WarningIcon style={{ fontSize: '1.2rem' }} />
                                {t(TK.conflictingAppointmentsWarning)}
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 'var(--spacer-M)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                                {modalConflicts.map(appt => (
                                    <li key={appt.id} style={{ fontSize: '0.875rem' }}>
                                        {appt.date} {appt.start}–{appt.end}
                                        {appt.clientName && ` · ${appt.clientName}`}
                                        {appt.type && ` · ${appt.type}`}
                                        {appt.professionalName && ` · ${appt.professionalName}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {!modalConflicts ? (<>
                        <AppButton color="secondary" onClick={() => setShowAddModal(false)}>{t(TK.cancel)}</AppButton>
                        <AppButton color="success" disabled={modalValidating} onClick={() => onConfirmAdd()}>{t(TK.add)}</AppButton>
                    </>) : (<>
                        <AppButton color="secondary" onClick={() => setModalConflicts(null)} style={{ marginRight: 'auto' }}>{t(TK.cancel)}</AppButton>
                        <AppButton color="warning" onClick={() => onConfirmAdd(true)}>{t(TK.bookAnyway)}</AppButton>
                    </>)}
                </ModalFooter>
            </Modal>
        )}
    </>
    );
}
